import { useEffect, useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessRecordByLevel,
  canCreate,
  canManageField,
  canMutate,
  isPermissionAllowed,
  normalizeApiPermissions,
} from '../utils/permissions';

/**
 * ثبت permissions برگشتی از یک API و برگرداندن helperهای UI
 * @param {string} resourceKey - مثلاً 'tickets'
 * @param {object|null|undefined} apiPermissions - آبجکت permissions از پاسخ API
 */
export function useResourcePermissions(resourceKey, apiPermissions) {
  const {
    setResourcePermissions,
    getResourcePermissions,
    isSuperAdmin,
    isAdmin,
    user: permissionsUser,
    permissionsRevision,
  } = usePermissions();
  const { user: authUser } = useAuth();
  const user = permissionsUser || authUser;

  useEffect(() => {
    if (apiPermissions && resourceKey) {
      setResourcePermissions(resourceKey, apiPermissions);
    }
  }, [apiPermissions, resourceKey, setResourcePermissions]);

  return useMemo(() => {
    const perms = normalizeApiPermissions(getResourcePermissions(resourceKey) || apiPermissions);
    const bypass = isSuperAdmin || isAdmin;
    const userId = user?.id;

    const level = (action) => (bypass ? 'all' : perms[action]);

    return {
      permissions: perms,
      canView: bypass || isPermissionAllowed(perms.view),
      canAdd: bypass || canCreate(perms.add),
      canEdit: bypass || canMutate(perms.edit),
      canDelete: bypass || canMutate(perms.delete),
      canManageAgent: bypass || canManageField(perms.manage_agent),
      canManage: (manageKey) => bypass || canManageField(perms[manageKey]),
      canEditRecord: (record) =>
        bypass ||
        canAccessRecordByLevel(perms.edit, {
          userId,
          ownerId: record?.user_id ?? record?.agent_id ?? record?.owner_id,
          addedById: record?.added_by ?? record?.created_by,
        }),
      canDeleteRecord: (record) =>
        bypass ||
        canAccessRecordByLevel(perms.delete, {
          userId,
          ownerId: record?.user_id ?? record?.agent_id ?? record?.owner_id,
          addedById: record?.added_by ?? record?.created_by,
        }),
      level,
    };
  }, [getResourcePermissions, resourceKey, apiPermissions, isSuperAdmin, isAdmin, user?.id, permissionsRevision]);
}

export default useResourcePermissions;
